import React, { useState } from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  Switch,
  FormControlLabel,
  Alert,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Api,
  Save,
  Add,
  Delete,
  ContentCopy,
  Visibility,
  VisibilityOff,
  Refresh
} from '@mui/icons-material';

const APISettings = () => {
  const [settings, setSettings] = useState({
    apiBaseUrl: 'http://localhost:8000',
    apiVersion: 'v1',
    enableCors: true,
    enableRateLimit: true,
    rateLimit: '100',
    rateLimitWindow: '60',
    enableApiKeys: true,
    enableLogging: true,
    logLevel: 'info'
  });

  const [apiKeys, setApiKeys] = useState([
    {
      id: 1,
      name: 'Production API Key',
      key: 'pk_live_51234567890abcdef',
      created: '2025-01-15',
      lastUsed: '2025-10-18',
      status: 'active'
    },
    {
      id: 2,
      name: 'Development API Key',
      key: 'pk_test_51234567890abcdef',
      created: '2025-01-10',
      lastUsed: '2025-10-17',
      status: 'active'
    }
  ]);

  const [showKey, setShowKey] = useState({});
  const [openDialog, setOpenDialog] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [success, setSuccess] = useState(false);

  const handleChange = (field) => (event) => {
    setSettings({
      ...settings,
      [field]: event.target.type === 'checkbox' ? event.target.checked : event.target.value
    });
  };

  const handleSave = () => {
    setSuccess(true);
    setTimeout(() => setSuccess(false), 3000);
  };

  const toggleShowKey = (id) => {
    setShowKey({ ...showKey, [id]: !showKey[id] });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setSuccess(true);
    setTimeout(() => setSuccess(false), 2000);
  };

  const handleGenerateKey = () => {
    if (!newKeyName) return;
    
    const newKey = {
      id: apiKeys.length + 1,
      name: newKeyName,
      key: `pk_${Date.now()}_${Math.random().toString(36).substring(7)}`,
      created: new Date().toISOString().split('T')[0],
      lastUsed: 'Never',
      status: 'active'
    };
    
    setApiKeys([...apiKeys, newKey]);
    setNewKeyName('');
    setOpenDialog(false);
    setSuccess(true);
    setTimeout(() => setSuccess(false), 3000);
  };

  const handleDeleteKey = (id) => {
    setApiKeys(apiKeys.filter(key => key.id !== id));
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
          API Configuration
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Configure API endpoints, authentication, and rate limiting
        </Typography>
      </Box>

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Settings saved successfully!
        </Alert>
      )}

      {/* API Configuration */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
            API Configuration
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="API Base URL"
                value={settings.apiBaseUrl}
                onChange={handleChange('apiBaseUrl')}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="API Version"
                value={settings.apiVersion}
                onChange={handleChange('apiVersion')}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.enableCors}
                    onChange={handleChange('enableCors')}
                  />
                }
                label="Enable CORS"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.enableApiKeys}
                    onChange={handleChange('enableApiKeys')}
                  />
                }
                label="Enable API Keys"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Rate Limiting */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
            Rate Limiting
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.enableRateLimit}
                    onChange={handleChange('enableRateLimit')}
                  />
                }
                label="Enable Rate Limiting"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Rate Limit (requests)"
                value={settings.rateLimit}
                onChange={handleChange('rateLimit')}
                type="number"
                disabled={!settings.enableRateLimit}
                helperText="Maximum requests per window"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Time Window (seconds)"
                value={settings.rateLimitWindow}
                onChange={handleChange('rateLimitWindow')}
                type="number"
                disabled={!settings.enableRateLimit}
                helperText="Time window for rate limit"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* API Logging */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
            API Logging
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.enableLogging}
                    onChange={handleChange('enableLogging')}
                  />
                }
                label="Enable API Logging"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Log Level"
                value={settings.logLevel}
                onChange={handleChange('logLevel')}
                disabled={!settings.enableLogging}
                SelectProps={{
                  native: true,
                }}
              >
                <option value="debug">Debug</option>
                <option value="info">Info</option>
                <option value="warning">Warning</option>
                <option value="error">Error</option>
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* API Keys Management */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              API Keys
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setOpenDialog(true)}
            >
              Generate New Key
            </Button>
          </Box>
          
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Name</strong></TableCell>
                  <TableCell><strong>API Key</strong></TableCell>
                  <TableCell><strong>Created</strong></TableCell>
                  <TableCell><strong>Last Used</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell align="center"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {apiKeys.map((key) => (
                  <TableRow key={key.id}>
                    <TableCell>{key.name}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {showKey[key.id] ? key.key : '••••••••••••••••'}
                        </Typography>
                        <IconButton size="small" onClick={() => toggleShowKey(key.id)}>
                          {showKey[key.id] ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                        <IconButton size="small" onClick={() => copyToClipboard(key.key)}>
                          <ContentCopy fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                    <TableCell>{key.created}</TableCell>
                    <TableCell>{key.lastUsed}</TableCell>
                    <TableCell>
                      <Chip 
                        label={key.status} 
                        color={key.status === 'active' ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton 
                        size="small" 
                        color="error"
                        onClick={() => handleDeleteKey(key.id)}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={() => window.location.reload()}
        >
          Reset
        </Button>
        <Button
          variant="contained"
          startIcon={<Save />}
          onClick={handleSave}
        >
          Save Settings
        </Button>
      </Box>

      {/* Generate API Key Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Generate New API Key</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Key Name"
            fullWidth
            value={newKeyName}
            onChange={(e) => setNewKeyName(e.target.value)}
            placeholder="e.g., Production API Key"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleGenerateKey} variant="contained">Generate</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default APISettings;
